'use client';

import React, { memo, useCallback, useRef, useState } from 'react';

import { Button } from '@repo/ui/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';

interface ArticleCardTitleProps {
  title: string;
}

const PureArticleCardTitle: React.FC<ArticleCardTitleProps> = ({ title }) => {
  const [isTitleTooltipOpen, setIsTitleTooltipOpen] = useState(false);

  const titleTooltipRef = useRef<HTMLSpanElement>(null);

  const handleTitleTooltipOpenChange = useCallback(
    (open: boolean) => {
      if (titleTooltipRef.current) {
        const isClamped =
          titleTooltipRef.current.scrollHeight >
          titleTooltipRef.current.clientHeight;
        setIsTitleTooltipOpen(open && isClamped);
      }
    },
    [setIsTitleTooltipOpen],
  );

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={200}
        open={isTitleTooltipOpen}
        onOpenChange={handleTitleTooltipOpenChange}
      >
        <TooltipTrigger asChild>
          <Button
            variant="link"
            className="group/article-link min-h-0 h-auto p-0"
          >
            <span
              ref={titleTooltipRef}
              className="line-clamp-5 whitespace-break-spaces text-left font-grotesk font-extrabold text-neutral-2 underline-offset-1 group-hover/article-link:underline text-[15px] leading-4 cred-md:text-lg cred-md:leading-5 cred-lg:text-[19px] cred-lg:leading-[21px] cred-xl:text-[21px] cred-xl:leading-[25px]"
            >
              {title}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] cred-md:max-w-80 cred-lg:max-w-[330px] cred-xl:max-w-[440px]">
          <p className="text-xs leading-none text-neutral-2 cred-md:text-sm cred-lg:text-base cred-xl:text-base">
            {title}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
export const ArticleCardTitle = memo(
  PureArticleCardTitle,
  (prevProps, nextProps) => {
    return prevProps.title === nextProps.title;
  },
);
